'use client';

import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { toast } from 'react-toastify';

import { LAUNCHPAD_FEE } from '@/lib/constants';
import {
  currentPhaseState,
  isUserPermittedState,
  phaseBidCountsState,
  selectedProjectBidStatsState,
  userSOLBalanceState,
  userTokenBalanceState,
  userRemainingBidsCountState,
} from '@/lib/state';
import { lamportsToSol, lamportsToToken } from '@/lib/utils';

import LoadingSpinner from 'components/LoadingSpinner';

export default function WLBidForm({ project, onSubmit }) {
  const balance = useRecoilValue(userSOLBalanceState);
  const tokenBalance = useRecoilValue(userTokenBalanceState);
  const currentPhase = useRecoilValue(currentPhaseState);
  const isPermitted = useRecoilValue(isUserPermittedState);
  const phaseBids = useRecoilValue(phaseBidCountsState);
  const userRemaining = useRecoilValue(userRemainingBidsCountState);
  const { min } = useRecoilValue(selectedProjectBidStatsState);
  const { bid_fee, token, decimals } = project;

  const price = useMemo(() => {
    if (currentPhase?.price) {
      return lamportsToToken(decimals, currentPhase.price);
    }
    if (min) {
      return lamportsToToken(decimals, min);
    }
    return null;
  }, [decimals, currentPhase, min]);

  const supply = useMemo(() => {
    if (!(currentPhase && phaseBids)) {
      return;
    }
    const { id, supply } = currentPhase;
    const { bids } = phaseBids.find((b) => b.id === id) || {};
    return `${bids || 0} / ${supply}`;
  }, [currentPhase, phaseBids]);

  const isBalanceSufficient = useMemo(() => {
    if (!(balance && price)) {
      return false;
    }
    if (token !== 'SOL') {
      return balance >= (bid_fee || LAUNCHPAD_FEE);
    }
    const b = lamportsToToken(decimals, balance + (bid_fee || LAUNCHPAD_FEE));
    return b >= price;
  }, [token, balance, price, decimals, bid_fee]);

  const isTokenBalanceSufficient = useMemo(() => {
    if (token === 'SOL') {
      return true; // Not using tokens on this auction
    }
    if (!(tokenBalance && price)) {
      return false;
    }
    const b = lamportsToToken(decimals, tokenBalance);
    return b >= price;
  }, [tokenBalance, price, decimals, token]);

  const onBidButtonPressed = useCallback(async () => {
    if (!isBalanceSufficient) {
      toast.warn('Insufficient SOL balance; add more funds!');
      return;
    }
    if (!isTokenBalanceSufficient) {
      toast.warn(`Insufficient ${token} balance; add more funds!`);
      return;
    }
    await onSubmit({ [price]: 1 });
  }, [token, onSubmit, price, isBalanceSufficient, isTokenBalanceSufficient]);

  if (!price) {
    return <LoadingSpinner size="8em" />;
  }

  return (
    <div className="flex flex-col w-full items-center space-y-4">
      <div className="flex items-center justify-between w-full">
        <h3 className="text-2xl">{currentPhase?.name} Phase</h3>
        <div className="tooltip cursor-pointer text-lg" data-tip="Bids received in this phase">
          [<span className="font-mono"> {supply} </span>]
        </div>
      </div>
      {isPermitted && userRemaining > 0 ? (
        <div
          className="tooltip tooltip-bottom cursor-pointer"
          data-tip={
            isBalanceSufficient && isTokenBalanceSufficient
              ? `+ ${lamportsToSol(bid_fee || LAUNCHPAD_FEE)} SOL fee`
              : 'Insufficient balance; add more funds!'
          }
        >
          {isBalanceSufficient ? (
            <button className="btn btn-wide btn-success" onClick={onBidButtonPressed}>
              Bid<span className="font-mono">&nbsp;{price}&nbsp;</span>
              {token}
            </button>
          ) : (
            <button className="btn btn-wide btn-disabled disabled">
              Bid<span className="font-mono">&nbsp;{price}&nbsp;</span>
              {token}
            </button>
          )}
        </div>
      ) : isPermitted && userRemaining < 1 ? (
        <div
          className="tooltip cursor-hover"
          data-tip="You have submitted all your allocated bids for this phase"
        >
          <button className="btn btn-wide btn-disabled disabled">
            {currentPhase?.name} Bid Accepted!
          </button>
        </div>
      ) : (
        <div className="flex flex-col w-full space-y-2">
          <p className="text-center text-xl">Not Permitted</p>
          <ul className="font-light list-disc ml-4">
            <li>Wallet has used all its allocated {currentPhase?.name || ''} bids, OR</li>
            <li>Wallet is not on the list of approved bidders</li>
          </ul>
        </div>
      )}
    </div>
  );
}
